import { computeBudgetFromParts, pctToFraction, roundMoney } from './budgetEngineV2';
import {
  legacyProductToBudgetParts,
  mapBudgetResultToLegacySimulationData,
} from './legacyPriceSimulateAdapter';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

function migrationMaintenanceHourPerHour(raw) {
  const m = raw.machine || {};
  if (m.equipment_maintenance_per_hour != null && m.equipment_maintenance_per_hour !== '') {
    return nf(m.equipment_maintenance_per_hour);
  }
  const monthly = nf(m.equipment_maintenance_per_month ?? m.maintenance_per_month);
  if (monthly <= 0) {
    return roundMoney(330 / 300);
  }
  const w = raw.work || {};
  const days = Math.max(1, ni(w.working_days_per_month) || 25);
  const hrs = Math.max(1, ni(w.working_hours_per_day) || 12);
  const hoursMonth = days * hrs;
  return roundMoney(monthly / hoursMonth);
}

function migrationReinvestmentPercent(raw) {
  const rp = raw.pricing || {};
  if (rp.reinvestment_percent != null && rp.reinvestment_percent !== '') {
    return Math.round(nf(rp.reinvestment_percent));
  }
  if (rp.investment_percent != null && rp.investment_percent !== '') {
    return Math.round(nf(rp.investment_percent));
  }
  return 10;
}

function migrationProfitMarginPercent(raw) {
  const rp = raw.pricing || {};
  if (rp.profit_margin_percent != null && rp.profit_margin_percent !== '') {
    return Math.round(nf(rp.profit_margin_percent));
  }
  return 0;
}

export function getDefaultConstants() {
  return normalizeConstants({
    materials: {
      PLA: { cost_per_kg: 85 },
      PETG: { cost_per_kg: 90 },
      TPU: { cost_per_kg: 120 },
    },
    machine: {
      equipment_maintenance_per_hour: roundMoney(330 / 300),
      equipment_degradation_per_hour: 0,
      energy_per_hour: 0.12,
      print_labor_per_hour: 0.5,
    },
    services: {
      modeling_per_hour: 20,
      painting_per_hour: 50,
    },
    percentages: {
      failure_percent: 5,
      marketing_percent: 0,
    },
    pricing: {
      delivery_fee_default: 0,
      reinvestment_percent: 10,
      profit_margin_percent: 0,
    },
  });
}

export function normalizeConstants(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const m = raw.machine || {};
  const rp = raw.pricing || {};

  const maintenanceHour = migrationMaintenanceHourPerHour(raw);
  const degradationHour = nf(m.equipment_degradation_per_hour);

  let reinvestmentPct = migrationReinvestmentPercent(raw);
  if (!Number.isFinite(reinvestmentPct) || reinvestmentPct < 0 || reinvestmentPct >= 100) {
    reinvestmentPct = 10;
  }

  let profitMarginPct = migrationProfitMarginPercent(raw);
  if (!Number.isFinite(profitMarginPct) || profitMarginPct < 0 || profitMarginPct >= 100) {
    profitMarginPct = 0;
  }

  return {
    materials: raw.materials && typeof raw.materials === 'object' ? raw.materials : {},
    machine: {
      equipment_maintenance_per_hour: maintenanceHour,
      equipment_degradation_per_hour: Math.max(0, degradationHour),
      energy_per_hour: nf(m.energy_per_hour),
      print_labor_per_hour: nf(m.print_labor_per_hour),
    },
    services: {
      modeling_per_hour: nf(raw.services?.modeling_per_hour),
      painting_per_hour: nf(raw.services?.painting_per_hour),
    },
    percentages: {
      failure_percent: Math.max(0, Math.round(nf(raw.percentages?.failure_percent))),
      marketing_percent: Math.max(0, Math.round(nf(raw.percentages?.marketing_percent))),
    },
    pricing: {
      delivery_fee_default: Math.max(0, nf(rp.delivery_fee_default)),
      reinvestment_percent: reinvestmentPct,
      profit_margin_percent: profitMarginPct,
    },
  };
}

function validateSimulation(constants, p) {
  if (!constants || !p) return 'dados inválidos';

  if (!constants.materials || Object.keys(constants.materials).length === 0) {
    return 'informe ao menos um material';
  }

  if (p.quantity < 1) return 'quantidade deve ser pelo menos 1';

  if (p.grams < 0 || p.print_hours < 0 || p.modeling_hours < 0 || p.painting_hours < 0) {
    return 'valores de tempo ou peso não podem ser negativos';
  }

  const vcList = Array.isArray(p.variable_costs) ? p.variable_costs : [];

  if (vcList.length > 40) return 'no máximo 40 custos variáveis';

  for (let i = 0; i < vcList.length; i++) {
    const row = vcList[i];
    const cn = String(row?.name || '').trim();
    const cu = nf(row?.cost_unit);

    if (cu < 0) {
      return `custo variável ${i + 1}: valores não podem ser negativos`;
    }

    if (cu > 0 && !cn) {
      return `custo variável ${i + 1}: informe o nome`;
    }
  }

  if (!p.material) return 'material é obrigatório';

  if (!constants.materials[p.material]) return 'material não encontrado nas configurações';

  const fp = constants.percentages.failure_percent;

  if (!Number.isInteger(fp) || fp < 0 || fp > 100) {
    return 'percentual de falha deve ser inteiro entre 0 e 100';
  }

  const mp = constants.percentages.marketing_percent;

  if (!Number.isInteger(mp) || mp < 0 || mp >= 100) {
    return 'percentual de marketing deve ser inteiro entre 0 e 99';
  }

  const marketingFrac = pctToFraction(constants.percentages.marketing_percent);

  if (marketingFrac >= 1) return 'percentual de marketing deve ser menor que 100%';

  const rPct = nf(constants.pricing?.reinvestment_percent);
  if (!Number.isInteger(rPct) || rPct < 0 || rPct >= 100) {
    return 'percentual de reinvestimento deve ser inteiro entre 0 e 99';
  }

  const pPct = nf(constants.pricing?.profit_margin_percent);
  if (!Number.isInteger(pPct) || pPct < 0 || pPct >= 100) {
    return 'percentual de margem de lucro deve ser inteiro entre 0 e 99';
  }

  return null;
}

export function computeSimulation(constantsInput, productInput) {
  const constants = normalizeConstants(constantsInput);
  if (!constants) return { ok: false, error: 'dados inválidos' };
  const err = validateSimulation(constants, productInput);
  if (err) return { ok: false, error: err };

  const parts = legacyProductToBudgetParts(constants, productInput);
  const engine = computeBudgetFromParts(parts);
  if (!engine.ok) {
    return { ok: false, error: engine.error };
  }
  const data = mapBudgetResultToLegacySimulationData(parts, engine.data);
  return { ok: true, data };
}

export function validateConstantsForSave(c) {
  const normalized = normalizeConstants(c);

  if (!normalized) return 'configuração vazia';

  if (!normalized.materials || Object.keys(normalized.materials).length === 0) {
    return 'informe ao menos um material';
  }

  for (const name of Object.keys(normalized.materials)) {
    if (!name.trim()) return 'nome de material não pode ser vazio';

    if (normalized.materials[name].cost_per_kg < 0) {
      return `custo por kg não pode ser negativo (${name})`;
    }
  }

  if (
    normalized.machine.equipment_maintenance_per_hour < 0 ||
    normalized.machine.equipment_degradation_per_hour < 0 ||
    normalized.machine.energy_per_hour < 0 ||
    normalized.machine.print_labor_per_hour < 0
  ) {
    return 'valores de equipamento não podem ser negativos';
  }

  if (normalized.services.modeling_per_hour < 0 || normalized.services.painting_per_hour < 0) {
    return 'valores de serviço não podem ser negativos';
  }

  if (
    !Number.isInteger(normalized.percentages.failure_percent) ||
    normalized.percentages.failure_percent < 0 ||
    normalized.percentages.failure_percent > 100
  ) {
    return 'percentual de falha deve ser inteiro entre 0 e 100';
  }

  if (
    !Number.isInteger(normalized.percentages.marketing_percent) ||
    normalized.percentages.marketing_percent < 0 ||
    normalized.percentages.marketing_percent >= 100
  ) {
    return 'percentual de marketing deve ser inteiro entre 0 e 99';
  }

  if (normalized.pricing.delivery_fee_default < 0) {
    return 'taxa de entrega padrão não pode ser negativa';
  }

  const rp = nf(normalized.pricing?.reinvestment_percent);
  if (!Number.isInteger(rp) || rp < 0 || rp >= 100) {
    return 'percentual de reinvestimento deve ser inteiro entre 0 e 99';
  }

  const pp = nf(normalized.pricing?.profit_margin_percent);
  if (!Number.isInteger(pp) || pp < 0 || pp >= 100) {
    return 'percentual de margem de lucro deve ser inteiro entre 0 e 99';
  }

  return null;
}
